import React, { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  TAgetComposedPipelineFlow,
  TAgetActiveRunpodPods,
  TAterminateRunpodPod,
  TAterminateAllRunpodPods,
  HashtagPipelineFlowData,
  RunpodPod,
} from "../../../services/hashtagPipelineAPI"

interface StatCardProps {
  title: string
  value: number | string
  subtitle?: string
  color?: "blue" | "green" | "yellow" | "red" | "purple" | "gray"
  percent?: number
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, color = "blue", percent }) => {
  const colorClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    green: "bg-green-50 border-green-200 text-green-800",
    yellow: "bg-yellow-50 border-yellow-200 text-yellow-800",
    red: "bg-red-50 border-red-200 text-red-800",
    purple: "bg-purple-50 border-purple-200 text-purple-800",
    gray: "bg-gray-50 border-gray-200 text-gray-800",
  }

  return (
    <div className={`p-4 rounded-lg border-2 ${colorClasses[color]}`}>
      <div className="text-sm font-medium opacity-80">{title}</div>
      <div className="text-2xl font-bold mt-1">
        {typeof value === "number" ? value.toLocaleString("tr-TR") : value}
      </div>
      {percent !== undefined && (
        <div className="mt-2 h-2 bg-white rounded-full overflow-hidden">
          <div
            className="h-full bg-current opacity-60 transition-all duration-500"
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {subtitle && <div className="text-xs mt-1 opacity-70">{subtitle}</div>}
    </div>
  )
}


const HashtagPipeline: React.FC = () => {
  const navigate = useNavigate()

  const [pipelineFlow, setPipelineFlow] = useState<HashtagPipelineFlowData | null>(null)
  const [pods, setPods] = useState<RunpodPod[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingPods, setIsLoadingPods] = useState(false)
  const [error, setError] = useState("")
  const [terminatingPod, setTerminatingPod] = useState<string | null>(null)
  const [terminatingAll, setTerminatingAll] = useState(false)

  const fetchPipelineFlow = useCallback(async () => {
    try {
      const data = await TAgetComposedPipelineFlow()
      setPipelineFlow(data)
      setError("")
    } catch (err: unknown) {
      if (
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        (err as { response?: { status?: number } }).response?.status === 401
      ) {
        navigate("/login")
        return
      }
      setError("Pipeline verileri yüklenirken hata oluştu")
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  const fetchPods = useCallback(async () => {
    setIsLoadingPods(true)
    try {
      const data = await TAgetActiveRunpodPods()
      setPods(data.pods)
    } catch (err) {
      console.error("Pods error:", err)
    } finally {
      setIsLoadingPods(false)
    }
  }, [])

  useEffect(() => {
    fetchPipelineFlow()
    fetchPods()

    // Her dakika otomatik güncelle
    const interval = setInterval(() => {
      fetchPipelineFlow()
      fetchPods()
    }, 60000)

    return () => clearInterval(interval)
  }, [fetchPipelineFlow, fetchPods])

  const handleTerminatePod = async (podId: string) => {
    if (!window.confirm(`${podId} pod'unu terminate etmek istediğinize emin misiniz?`)) {
      return
    }

    setTerminatingPod(podId)
    try {
      await TAterminateRunpodPod(podId)
      setPods((prev) => prev.filter((p) => p.id !== podId))
    } catch (err) {
      console.error("Terminate error:", err)
      alert("Pod terminate edilirken hata oluştu")
    } finally {
      setTerminatingPod(null)
    }
  }

  const handleTerminateAll = async () => {
    if (!window.confirm(`Tüm ${pods.length} pod'u terminate etmek istediğinize emin misiniz?`)) {
      return
    }

    setTerminatingAll(true)
    try {
      await TAterminateAllRunpodPods()
      setPods([])
      fetchPipelineFlow()
    } catch (err) {
      console.error("Terminate all error:", err)
      alert("Podlar terminate edilirken hata oluştu")
    } finally {
      setTerminatingAll(false)
    }
  }

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return `${hours}s ${minutes}dk`
  }

  const hashtagPostAnalyzedPercent = pipelineFlow?.hashtagPostCounts.totalHashtagPosts
    ? Math.round(
        (pipelineFlow.hashtagPostCounts.hashtagPostsAnalyzed /
          pipelineFlow.hashtagPostCounts.totalHashtagPosts) *
          100,
      )
    : 0

  const hashtagAnalysisCompletedPercent = pipelineFlow?.hashtagAnalysisJob.totalPosts
    ? Math.round(
        (pipelineFlow.hashtagAnalysisJob.completed / pipelineFlow.hashtagAnalysisJob.totalPosts) *
          100,
      )
    : 0

  const productEmbeddingCompletedPercent = pipelineFlow?.productEmbeddingJob.totalPosts
    ? Math.round(
        (pipelineFlow.productEmbeddingJob.completed / pipelineFlow.productEmbeddingJob.totalPosts) *
          100,
      )
    : 0

  const influencerScrapeCompletedPercent = pipelineFlow?.influencerScrape.total
    ? Math.round(
        (pipelineFlow.influencerScrape.completed / pipelineFlow.influencerScrape.total) * 100,
      )
    : 0

  const gpuAnalysisPercent = pipelineFlow?.instaPostStats.totalPosts
    ? Math.round(
        (pipelineFlow.instaPostStats.gpuAnalyzedPosts / pipelineFlow.instaPostStats.totalPosts) *
          100,
      )
    : 0

  const savedPercent = pipelineFlow?.instaPostStats.totalPosts
    ? Math.round(
        (pipelineFlow.instaPostStats.savedPosts / pipelineFlow.instaPostStats.totalPosts) * 100,
      )
    : 0

  const fmt = (v: number | string | undefined | null) =>
    typeof v === "number" ? v.toLocaleString("tr-TR") : v ?? "-"

  const renderStatusBadge = (status: "completed" | "in_progress" | "pending") => {
    const config = {
      completed: { label: "Tamam", icon: "✅", cls: "bg-green-100 text-green-800" },
      in_progress: { label: "Devam", icon: "⏳", cls: "bg-yellow-100 text-yellow-800" },
      pending: { label: "Bekliyor", icon: "🔴", cls: "bg-red-100 text-red-800" },
    }
    const c = config[status]
    return (
      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${c.cls}`}>
        {c.icon} {c.label}
      </span>
    )
  }

  const pipelineTableData: {
    num: number
    title: string
    desc: string
    status: "completed" | "in_progress" | "pending"
    total: number | string
    completed: number | string
    waiting: number | string
    waitingExtra?: string
    percent: number
  }[] = pipelineFlow
    ? [
        {
          num: 1,
          title: "Hashtag Managers",
          desc: "Job durumları ve aktiflik",
          status: pipelineFlow.hashtagManagers.active
            ? "in_progress"
            : pipelineFlow.hashtagManagers.completed
              ? "completed"
              : "pending",
          total: pipelineFlow.hashtagManagers.total,
          completed: pipelineFlow.hashtagManagers.completed,
          waiting: pipelineFlow.hashtagManagers.active,
          percent: pipelineFlow.hashtagManagers.activePercent,
        },
        {
          num: 2,
          title: "Hashtag Post Sayıları",
          desc: "Analiz ihtiyacı olan postlar",
          status: pipelineFlow.hashtagPostCounts.totalHashtagPosts
            ? pipelineFlow.hashtagPostCounts.hashtagPostsNeedingAnalysis === 0
              ? "completed"
              : "in_progress"
            : "pending",
          total: pipelineFlow.hashtagPostCounts.totalHashtagPosts,
          completed: pipelineFlow.hashtagPostCounts.hashtagPostsAnalyzed,
          waiting: pipelineFlow.hashtagPostCounts.hashtagPostsNeedingAnalysis,
          percent: hashtagPostAnalyzedPercent,
        },
        {
          num: 3,
          title: "Hashtag Analysis Job",
          desc: "Video analiz kuyruğu",
          status: pipelineFlow.hashtagAnalysisJob.totalPosts
            ? pipelineFlow.hashtagAnalysisJob.waiting === 0 &&
              pipelineFlow.hashtagAnalysisJob.pending === 0
              ? "completed"
              : "in_progress"
            : "pending",
          total: pipelineFlow.hashtagAnalysisJob.totalPosts,
          completed: pipelineFlow.hashtagAnalysisJob.completed,
          waiting: pipelineFlow.hashtagAnalysisJob.waiting,
          waitingExtra: `${pipelineFlow.hashtagAnalysisJob.pending.toLocaleString("tr-TR")} pending`,
          percent: hashtagAnalysisCompletedPercent,
        },
        {
          num: 4,
          title: "Product Embedding",
          desc: "Product embedding kuyruğu",
          status: pipelineFlow.productEmbeddingJob.totalPosts
            ? pipelineFlow.productEmbeddingJob.waiting === 0
              ? "completed"
              : "in_progress"
            : "pending",
          total: pipelineFlow.productEmbeddingJob.totalPosts,
          completed: pipelineFlow.productEmbeddingJob.completed,
          waiting: pipelineFlow.productEmbeddingJob.waiting,
          percent: productEmbeddingCompletedPercent,
        },
        {
          num: 5,
          title: "Influencer Scrape",
          desc: "Influencer scrape kuyruğu",
          status: pipelineFlow.influencerScrape.total
            ? pipelineFlow.influencerScrape.waiting === 0
              ? "completed"
              : "in_progress"
            : "pending",
          total: pipelineFlow.influencerScrape.total,
          completed: pipelineFlow.influencerScrape.completed,
          waiting: pipelineFlow.influencerScrape.waiting,
          percent: influencerScrapeCompletedPercent,
        },
        {
          num: 6,
          title: "Tagged Profiles",
          desc: "Etiket profilleri -> Marka ekleme",
          status: "in_progress",
          total: pipelineFlow.hashtagBrands.total,
          completed: pipelineFlow.taggedProfiles?.postsWithTags || 0,
          waiting: "-",
          waitingExtra: `${pipelineFlow.hashtagManagers.total.toLocaleString("tr-TR")} manager`,
          percent: 0,
        },
        {
          num: 7,
          title: "Insta Post İstatistikleri",
          desc: "Kayıt durumu",
          status: pipelineFlow.instaPostStats.totalPosts
            ? pipelineFlow.instaPostStats.unsavedPosts === 0
              ? "completed"
              : "in_progress"
            : "pending",
          total: pipelineFlow.instaPostStats.totalPosts,
          completed: pipelineFlow.instaPostStats.savedPosts,
          waiting: pipelineFlow.instaPostStats.unsavedPosts,
          waitingExtra: `${(pipelineFlow.instaPostStats.hashtagBrandPosts || 0).toLocaleString("tr-TR")} HB post`,
          percent: savedPercent,
        },
        {
          num: 8,
          title: "GPU Analizi (RunPod)",
          desc: "Görsel ve ses analizi",
          status:
            pods.length > 0
              ? "in_progress"
              : pipelineFlow.instaPostStats.noGpuAnalyzedPosts === 0
                ? "completed"
                : "pending",
          total:
            (pipelineFlow.instaPostStats.gpuAnalyzedPosts || 0) +
            (pipelineFlow.instaPostStats.noGpuAnalyzedPosts || 0),
          completed: pipelineFlow.instaPostStats.gpuAnalyzedPosts,
          waiting: pipelineFlow.instaPostStats.noGpuAnalyzedPosts,
          waitingExtra: `${pods.length} pod aktif`,
          percent: gpuAnalysisPercent,
        },
        {
          num: 9,
          title: "GPT Brand Analysis",
          desc: "Sektör ve tip analizi",
          status:
            pipelineFlow.hashtagBrands.withGptAnalysis > 0
              ? pipelineFlow.hashtagBrands.withoutGptAnalysis === 0
                ? "completed"
                : "in_progress"
              : "pending",
          total: pipelineFlow.hashtagBrands.total,
          completed: pipelineFlow.hashtagBrands.withGptAnalysis,
          waiting: pipelineFlow.hashtagBrands.withoutGptAnalysis,
          percent: pipelineFlow.hashtagBrands.gptAnalysisPercent,
        },
      ]
    : []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-pink-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Hashtag Pipeline Durumu</h1>
        <div className="flex gap-2">
          <button
            onClick={() => {
              fetchPipelineFlow()
              fetchPods()
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow transition"
          >
            Yenile
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Genel Özet */}
      <div className="mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Genel Özet</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            title="Toplam Marka"
            value={pipelineFlow?.hashtagBrands.total || 0}
            color="blue"
          />
          <StatCard
            title="Post Çekilmiş"
            value={pipelineFlow?.hashtagBrands.withPosts || 0}
            color="purple"
            percent={pipelineFlow?.hashtagBrands.postsPercent}
          />
          <StatCard
            title="GPT Analizi"
            value={pipelineFlow?.hashtagBrands.withGptAnalysis || 0}
            color="green"
            percent={pipelineFlow?.hashtagBrands.gptAnalysisPercent}
          />
          <StatCard
            title="Toplam Post"
            value={pipelineFlow?.instaPostStats.totalPosts || "-"}
            color="gray"
          />
          <StatCard
            title="GPU Analizi"
            value={pipelineFlow?.instaPostStats.gpuAnalyzedPosts || "-"}
            color="green"
            percent={gpuAnalysisPercent}
          />
        </div>
      </div>

      {/* Pipeline Adımları Tablosu */}
      <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-700">Pipeline Adımları</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-10">
                  #
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Adım
                </th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-28">
                  Durum
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                  Toplam
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                  Tamamlanan
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                  Bekleyen
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase w-36">
                  Oran
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pipelineTableData.map((row) => (
                <tr key={row.num} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-sm font-bold text-gray-400">{row.num}</td>
                  <td className="px-3 py-3">
                    <div className="text-sm font-medium text-gray-800">{row.title}</div>
                    <div className="text-xs text-gray-500">{row.desc}</div>
                  </td>
                  <td className="px-3 py-3 text-center">{renderStatusBadge(row.status)}</td>
                  <td className="px-3 py-3 text-right text-sm font-semibold text-gray-700">
                    {fmt(row.total)}
                  </td>
                  <td className="px-3 py-3 text-right text-sm text-green-700 font-medium">
                    {fmt(row.completed)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="text-sm text-yellow-700 font-medium">{fmt(row.waiting)}</div>
                    {row.waitingExtra && (
                      <div className="text-xs text-gray-500">{row.waitingExtra}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${row.percent}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-600 w-8 text-right">
                        {row.percent}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detay Tabloları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Insta Post Detay */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Insta Post Detayları</h3>
          </div>
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Toplam Post</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {fmt(pipelineFlow?.instaPostStats.totalPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Kaydedilen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.instaPostStats.savedPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Kaydedilmeyen</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-red-700">
                  {fmt(pipelineFlow?.instaPostStats.unsavedPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">HashtagBrand Post</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-purple-700">
                  {fmt(pipelineFlow?.instaPostStats.hashtagBrandPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">GPU Analizi Yapılmış</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.instaPostStats.gpuAnalyzedPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">GPU Analizi Eksik</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-red-700">
                  {fmt(pipelineFlow?.instaPostStats.noGpuAnalyzedPosts)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* GPU & RunPod Detay */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">GPU & RunPod Detayları</h3>
          </div>
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Aktif Pod</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {pods.length}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">DB Worker (Aktif)</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {fmt(pipelineFlow?.runpodWorkers.active)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">DB Worker (Çalışan)</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.runpodWorkers.running)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">DB Worker (Boşta)</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-yellow-700">
                  {fmt(pipelineFlow?.runpodWorkers.idle)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Etiketli Post</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-purple-700">
                  {fmt(pipelineFlow?.taggedProfiles?.postsWithTags)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Marka Detay */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Marka Detayları</h3>
          </div>
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Toplam Marka</td>
                <td className="px-4 py-2 text-xs font-semibold text-right">
                  {fmt(pipelineFlow?.hashtagBrands.total)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">GPT Analizi Yapılmış</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.hashtagBrands.withGptAnalysis)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">GPT Analizi Eksik</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-red-700">
                  {fmt(pipelineFlow?.hashtagBrands.withoutGptAnalysis)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Post Çekilmiş</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-green-700">
                  {fmt(pipelineFlow?.hashtagBrands.withPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Post Çekilmemiş</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-red-700">
                  {fmt(pipelineFlow?.hashtagBrands.withoutPosts)}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs text-gray-600">Aktif Scrape Job</td>
                <td className="px-4 py-2 text-xs font-semibold text-right text-yellow-700">
                  {fmt(pipelineFlow?.hashtagManagers.active)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* RunPod Pods Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-700">
            Aktif RunPod Podları ({pods.length})
            {isLoadingPods && <span className="ml-2 text-sm text-gray-500">(yükleniyor...)</span>}
          </h2>
          {pods.length > 0 && (
            <button
              onClick={handleTerminateAll}
              disabled={terminatingAll}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg shadow transition"
            >
              {terminatingAll ? "Terminate ediliyor..." : "Tümünü Kapat"}
            </button>
          )}
        </div>

        {pods.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            Aktif pod bulunmuyor
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Pod ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    İsim
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    GPU
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Çalışma Süresi
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pods.map((pod) => (
                  <tr key={pod.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-700">{pod.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{pod.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{pod.gpuCount}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatUptime(pod.uptimeSeconds)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleTerminatePod(pod.id)}
                        disabled={terminatingPod === pod.id}
                        className="px-3 py-1 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded disabled:opacity-50"
                      >
                        {terminatingPod === pod.id ? "..." : "Kapat"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Last Updated */}
      {pipelineFlow?.lastUpdated && (
        <div className="text-sm text-gray-500 text-right">
          Son güncelleme: {new Date(pipelineFlow.lastUpdated).toLocaleString("tr-TR")}
        </div>
      )}
    </div>
  )
}

export default HashtagPipeline
